import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import agentService, { setAuthContext } from '../services/agentService';
import tokenService from '../services/tokenService';
import { useAuth } from '../contexts/AuthContext';

interface ConsoleInterfaceProps {
  onLogin?: () => void;
}

interface ConsoleInputContainerProps {
  $disabled?: boolean;
}

const ConsoleContainer = styled.div`
  width: 90%;
  height: 70%;
  background-color: #111;
  border-radius: 5px;
  border: 1px solid ${props => props.theme.colors.primary};
  padding: 1rem;
  font-family: ${props => props.theme.fonts.main};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 192, 239, 0.3);
  position: relative;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    padding: 0.75rem;
  }
`;

const ConsoleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    padding-bottom: 0.3rem;
    margin-bottom: 0.5rem;
  }
`;

const ConsoleTitle = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const TokenLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TokenLink = styled.a`
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-size: 0.7rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
    color: ${props => props.theme.colors.primary};
  }
`;

// Blockchain explorer icons
const SolscanIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.243 18.788L6.55 15.54l4.207-2.436 4.208 2.436-4.208 3.248zm.001-5.248L6.55 10.292l4.208-7.292 4.208 7.292-4.208 3.248z" />
  </svg>
);

const DexscreenerIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-2 16h4v2h-4v-2zm6-3H8v-2h8v2zm0-3H8V8h8v2z" />
  </svg>
);

interface ConsoleStatusProps {
  isSessionActive?: boolean;
}

const ConsoleStatus = styled.div<ConsoleStatusProps>`
  color: ${props => {
    if (props.isSessionActive) return '#00FF00';
    return props.theme.colors.success;
  }};
  font-size: 0.8rem;
  margin-right: 0.5rem;
`;

const ConsoleOutput = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  line-height: 1.4;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }
`;

const ConsoleInputContainer = styled.div<ConsoleInputContainerProps>`
  display: flex;
  align-items: center;
  opacity: ${props => props.$disabled ? 0.5 : 1};
`;

const Prompt = styled.span`
  color: ${props => props.theme.colors.primary};
  margin-right: 0.5rem;
`;

interface ConsoleInputProps {
  disabled?: boolean;
}

const ConsoleInput = styled.input<ConsoleInputProps>`
  flex: 1;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.9rem;
  outline: none;
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const ConsoleMessage = styled.div`
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 0.4rem;
  }
`;

const Command = styled.div`
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const Response = styled.div`
  color: ${props => props.theme.colors.text};
  opacity: 0.8;
  white-space: pre-wrap;
`;

const demoResponses = [
  "Initializing Voovo Trading Bot v1.0.0...",
  "Connected to Solana blockchain network.",
  "Type 'help' to see available commands.",
  "Please use the 'Connect Wallet / Login' button above or type 'login' to start trading."
];

const ClearButton = styled.button`
  background-color: transparent;
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.primary};
  padding: 0.3rem 0.8rem;
  border-radius: 3px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 0.5rem;
  
  &:hover {
    background-color: rgba(0, 192, 239, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
    margin-left: 0.3rem;
  }
`;

const ConsoleInterface: React.FC<ConsoleInterfaceProps> = ({ onLogin }) => {
  const [input, setInput] = useState<string>('');
  const [history, setHistory] = useState<Array<{ type: 'command' | 'response', text: string }>>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const outputRef = useRef<HTMLDivElement>(null);
  const auth = useAuth();
  const { user, isAuthenticated } = auth;
  const isLoggedIn = isAuthenticated && user && user.privyUserId;

  useEffect(() => {
    setAuthContext(auth);
  }, [auth]);

  useEffect(() => {
    if (!initialized) {
      let timeout = 300;
      const messagesToShow = isLoggedIn ? demoResponses.slice(0, 3) : demoResponses;

      messagesToShow.forEach((response) => {
        setTimeout(() => {
          setHistory(prev => [...prev, { type: 'response', text: response }]);
        }, timeout);
        timeout += 300;
      });
      
      setInitialized(true);
    }
  }, [isLoggedIn, initialized]);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleInputSubmit = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim() !== '' && isLoggedIn && !isProcessing) {
      const commandText = input.trim();
      setHistory(prev => [...prev, { type: 'command', text: commandText }]);
      setInput('');
      setIsProcessing(true);

      try {
        // Special client-side commands
        if (commandText.toLowerCase() === 'login' && onLogin && !isLoggedIn) {
          onLogin();
          setHistory(prev => [...prev, { type: 'response', text: "Attempting login..." }]);
          setIsProcessing(false);
          return;
        }
        
        if (commandText.toLowerCase() === 'clear') {
          handleClearChat(); // Use the existing clear chat function
          setIsProcessing(false);
          return;
        }

        if (commandText.toLowerCase() === 'help') {
          const helpText = [
            "Available commands:",
            " - 'portfolio': View your current portfolio.",
            " - 'trade <TOKEN_SYMBOL> <AMOUNT> <SIDE (buy/sell)>': Execute a trade.",
            " - 'balance': Check your current wallet balance.",
            " - 'market <TOKEN_PAIR>': Get market information for a token pair.",
            " - 'history': View your trade history.",
            " - 'settings': View or modify bot settings.",
            " - 'status': Check the bot's current status.",
            " - 'clear': Clear the console output.",
            " - 'logout': Log out from the console (uses sidebar button).",
            "---",
            "For AI-assisted trading, describe your goal, e.g.:",
            "\"Buy 10 SOL when its price drops by 5%\"",
            "\"What are the top performing tokens today?\"",
            "\"Set a stop-loss for my BTC position at $60000\"",
          ].join('\n');
          setHistory(prev => [...prev, { type: 'response', text: helpText }]);
          setIsProcessing(false);
          return;
        }
        
        if (commandText.toLowerCase() === 'logout') {
          setHistory(prev => [...prev, { type: 'response', text: "Please use the Logout button in the sidebar to log out." }]);
          setIsProcessing(false);
          return;
        }

        // Ensure user and privyUserId are available
        if (!user || !user.privyUserId) {
          throw new Error("User not properly authenticated or privyUserId is missing.");
        }
        const userId = user.privyUserId;

        setHistory(prev => [...prev, { type: 'response', text: "Processing..." }]);
        
        // Call the agent service (assuming agentService.sendMessage is the correct method)
        const response = await agentService.sendMessage(commandText, userId);

        // Remove "Processing..." message
        setHistory(prev => prev.slice(0, -1));

        // Update session status based on agent's response
        setIsSessionActive(agentService.isSessionActive()); // or response.needs_more_info

        if (response.follow_up_question) {
          setHistory(prev => [...prev, { type: 'response', text: response.follow_up_question || "" }]);
        } else {
          setHistory(prev => [...prev, { type: 'response', text: response.message || "" }]);
        }

        if (response.action_required && response.action_type && response.action_details) {
          const actionDescription = getActionDescription(response.action_type, response.action_details);
          setHistory(prev => [...prev, { type: 'response', text: `Action Required: ${actionDescription}` }]);
          
          try {
            // Execute the blockchain action
            response.action_details.transaction_type = response.intent;
            const actionResult = await agentService.executeBlockchainAction(
              response.action_type,
              response.action_details,
              userId,
              response.session_id
            );
            
            // Debug: log the full action result to console
            console.log('Blockchain action result:', actionResult);
            
            // Display the result
            if (actionResult.success) {
              // If action was successful, display the success message
              setHistory(prev => [...prev, { 
                type: 'response', 
                text: `✅ ${actionResult.message || 'Action completed successfully.'}` 
              }]);
              
              // For check_balance actions, we need to handle SOL balance specially
              if (response.action_type === 'check_balance') {
                // Check if we have details either from the actionResult or a direct SOL balance
                if (actionResult.details) {
                  // Debug details object
                  console.log('Balance details:', actionResult.details);
                  
                  // New structure: details contains amount and token
                  const { amount, token, wallet_address } = actionResult.details;
                  
                  if (amount !== undefined) {
                    setHistory(prev => [...prev, { 
                      type: 'response', 
                      text: `Your ${token || 'SOL'} balance is: ${amount} ${token || 'SOL'}` 
                    }]);
                    
                    if (wallet_address) {
                      setHistory(prev => [...prev, { 
                        type: 'response', 
                        text: `Wallet address: ${wallet_address}` 
                      }]);
                    }
                  }
                } 
                // Direct SOL balance (sometimes returned directly)
                else if ((actionResult as any).sol_balance !== undefined) {
                  setHistory(prev => [...prev, { 
                    type: 'response', 
                    text: `Your SOL balance is: ${(actionResult as any).sol_balance} SOL` 
                  }]);
                }
                // Handle wallet balance format (might be under a different property)
                else if ((actionResult as any).wallet_balance !== undefined) {
                  setHistory(prev => [...prev, { 
                    type: 'response', 
                    text: `Your wallet balance is: ${(actionResult as any).wallet_balance} SOL` 
                  }]);
                }
                // If no specific balance info found, try to extract it from the message
                else if (actionResult.message && actionResult.message.includes('balance')) {
                  setHistory(prev => [...prev, { 
                    type: 'response', 
                    text: actionResult.message 
                  }]);
                }
                // If all else fails, show a fallback message
                else {
                  setHistory(prev => [...prev, { 
                    type: 'response', 
                    text: `Your balance information has been retrieved. Please check your wallet for the most up-to-date amount.` 
                  }]);
                }
              }
              
              // Show transaction hash for transactions if available
              if (actionResult.tx_hash) {
                setHistory(prev => [...prev, { 
                  type: 'response', 
                  text: `Transaction Hash: ${actionResult.tx_hash}` 
                }]);
              }
            } else {
              // If action failed, display the error
              setHistory(prev => [...prev, { 
                type: 'response', 
                text: `❌ ${actionResult.message || 'Action failed.'}` 
              }]);
              
              if (actionResult.error) {
                setHistory(prev => [...prev, { 
                  type: 'response', 
                  text: `Error: ${actionResult.error}` 
                }]);
              }
            }
          } catch (actionError: any) {
            // Handle any errors during action execution
            console.error("Error executing blockchain action:", actionError);
            
            setHistory(prev => [...prev, { 
              type: 'response', 
              text: `❌ Failed to execute blockchain action: ${actionError.message || 'Unknown error'}` 
            }]);
          }
        }
        
        if (!agentService.isSessionActive()) { // or !response.needs_more_info
             setHistory(prev => [...prev, { type: 'response', text: "Task Completed. Anything else I can help you with?" }]);
        }


      } catch (err: any) {
        // Remove "Processing..." message if it's still there
        setHistory(prev => prev.filter(item => item.text !== "Processing..."));

        console.error("Error processing command:", err);
        let errorMessage = "An error occurred while processing your command.";

        if (err.response && err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        setHistory(prev => [...prev, { type: 'response', text: `Error: ${errorMessage}` }]);
        
        // Handle token expiration / session invalidation
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          setIsSessionActive(false);
          setHistory(prev => [...prev, { type: 'response', text: "Your session may have expired. Please log in again." }]);
          // Consider calling auth.logout() or prompting for re-login via onLogin more directly
          if (auth.logout) auth.logout(); // Example: force logout
          if (onLogin) onLogin(); // Example: prompt privy login
        }
      } finally {
        setIsProcessing(false);
      }
    } else if (e.key === 'Enter' && !isLoggedIn) {
      setHistory(prev => [...prev, { type: 'response', text: "Please log in to use the console. You can log in using the button in the sidebar or by typing 'login'." }]);
      if (onLogin) {
        // Optionally trigger onLogin if user attempts to interact while not logged in
        // onLogin(); 
      }
    }
  };
  
  const handleLogin = () => {
    if (onLogin) {
      onLogin();
    }
  };

  const handleClearChat = () => {
    setHistory([]);
    let timeout = 0;
    const messagesToShow = isLoggedIn ? demoResponses.slice(0,3) : demoResponses;
    messagesToShow.forEach((response) => {
        setTimeout(() => {
            setHistory(prev => [...prev, { type: 'response', text: response }]);
        }, timeout);
        timeout += 300;
    });
  };
  
  const getActionDescription = (
    actionType: string, 
    details: Record<string, any>
  ): string => {
    switch (actionType) {
      case 'CONFIRM_TRADE':
        return `Confirm trade: ${details.side} ${details.amount} ${details.token} at estimated price ${details.estimatedPrice}. Type 'confirm' or 'cancel'.`;
      case 'APPROVE_TRANSACTION':
        return `Approve transaction: ${details.transactionType} for ${details.asset || ''} amount ${details.amount || ''}. Check your wallet.`;
      case 'TWO_FACTOR_AUTH':
        return `Enter your 2FA code for action: ${details.action}.`;
      default:
        return '';
    }
  };

  return (
    <ConsoleContainer>
      <ConsoleHeader>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ConsoleTitle>Voovo AI Terminal</ConsoleTitle>
          <TokenLinks>
            <TokenLink href="https://solscan.io/token/H6dZStmJBb5c6YAsnV5JxTbgwJiLjT5zKc4GeBwcpump" target="_blank" rel="noopener noreferrer">
              <SolscanIcon /> Token
            </TokenLink>
            <TokenLink href="https://dexscreener.com/solana/GjkTe6az5xLXpe4NUAtEc1fLxoLkXDH62nUZBxHZtCoC" target="_blank" rel="noopener noreferrer">
              <DexscreenerIcon /> Chart
            </TokenLink>
          </TokenLinks>
        </div>
        <div>
          {isLoggedIn && <ConsoleStatus isSessionActive={isSessionActive}>Session: {isSessionActive ? 'Active' : 'Inactive'}</ConsoleStatus>}
          <ClearButton onClick={handleClearChat} title="Clear console history">Clear</ClearButton>
        </div>
      </ConsoleHeader>
      <ConsoleOutput ref={outputRef}>
        {history.map((item, index) => (
          <ConsoleMessage key={index}>
            {item.type === 'command' ? (
              <Command><span>&gt;</span> {item.text}</Command>
            ) : (
              <Response>{item.text}</Response>
            )}
          </ConsoleMessage>
        ))}
      </ConsoleOutput>
      <ConsoleInputContainer $disabled={!isLoggedIn || isProcessing}>
        <Prompt>&gt;</Prompt>
        <ConsoleInput
          type="text"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleInputSubmit}
          placeholder={isLoggedIn ? "Type a command or ask AI..." : "Click 'Connect Wallet / Login' above or type 'login'"}
          disabled={!isLoggedIn || isProcessing}
        />
      </ConsoleInputContainer>
    </ConsoleContainer>
  );
};

export default ConsoleInterface; 